import {
  Engine,
  Scene,
  ArcRotateCamera,
  HemisphericLight,
  Vector3,
  Color3,
  Color4,
  MeshBuilder,
  StandardMaterial,
  DynamicTexture,
  type Mesh,
} from '@babylonjs/core';
import type { ITableDef, TargetDef, HoleDef } from '../core/types';

/** References to created table meshes for physics setup */
export interface TableMeshes {
  board: Mesh;
  walls: Mesh[];
  ball: Mesh;
  targets: Map<string, Mesh>;
  holes: Map<string, Mesh>;
  finish: Mesh;
  blobShadow: Mesh;
}

/**
 * Manages the Babylon.js scene, camera, and basic rendering setup.
 */
export class SceneManager {
  readonly engine: Engine;
  readonly scene: Scene;
  readonly camera: ArcRotateCamera;

  private resizeObserver: ResizeObserver | null = null;
  private tableMeshes: TableMeshes | null = null;
  private accentColor: string = '#ffd700';

  constructor(canvas: HTMLCanvasElement) {
    // Create engine with WebGL2, handle context loss gracefully
    this.engine = new Engine(canvas, true, {
      preserveDrawingBuffer: false,
      stencil: true,
      antialias: true,
      powerPreference: 'high-performance',
    });

    // Create scene
    this.scene = new Scene(this.engine);
    // Soft horizon background color (Deep slate blue)
    this.scene.clearColor = new Color4(0.1, 0.1, 0.15, 1);

    // Camera: looking down at the table (nearly top-down for labyrinth feel)
    // Positive Z at bottom of screen (where flippers/drain are) = standard pinball view
    this.camera = new ArcRotateCamera(
      'camera',
      Math.PI / 2,  // alpha: positive Z at bottom (pinball orientation)
      0.3,          // beta: nearly top-down
      20,
      Vector3.Zero(),
      this.scene
    );
    this.camera.lowerRadiusLimit = 5;
    this.camera.upperRadiusLimit = 40;
    this.camera.lowerBetaLimit = 0.1;
    this.camera.upperBetaLimit = Math.PI / 2;
    this.camera.fov = 0.6;

    // Disable camera controls for gameplay
    this.camera.attachControl(canvas, false);
    this.camera.inputs.clear();

    // Soft Hemispheric light for wrap-around lighting
    const ambient = new HemisphericLight(
      'ambient',
      new Vector3(0, 1, 0),
      this.scene
    );
    ambient.intensity = 0.8;
    ambient.diffuse = new Color3(0.95, 0.95, 1.0); // Soft sky light
    ambient.groundColor = new Color3(0.2, 0.2, 0.25); // Soft ground bounce

    // Handle resize
    this.resizeObserver = new ResizeObserver(() => {
      this.engine.resize();
    });
    this.resizeObserver.observe(canvas);
  }

  /** Dispose old table meshes (call before loading a new table) */
  clearTable(): void {
    if (!this.tableMeshes) return;

    // Dispose all meshes
    this.tableMeshes.board.dispose();
    this.tableMeshes.ball.dispose();
    this.tableMeshes.finish.dispose();
    this.tableMeshes.blobShadow.dispose();

    for (const wall of this.tableMeshes.walls) {
      wall.dispose();
    }
    for (const [, mesh] of this.tableMeshes.targets) {
      mesh.dispose();
    }
    for (const [, mesh] of this.tableMeshes.holes) {
      mesh.dispose();
    }

    this.tableMeshes = null;
  }

  /** Create all table geometry and return mesh references */
  createTable(table: ITableDef): TableMeshes {
    // Clean up old table first
    this.clearTable();
    this.accentColor = table.theme?.accentColor ?? '#ffd700';
    const walls: Mesh[] = [];
    const targets = new Map<string, Mesh>();
    const holes = new Map<string, Mesh>();

    // Board surface
    const board = MeshBuilder.CreateBox(
      'board',
      {
        width: table.width,
        height: 0.2,
        depth: table.length,
      },
      this.scene
    );
    board.position.y = -0.1;

    const boardMat = new StandardMaterial('boardMat', this.scene);
    boardMat.diffuseColor = Color3.FromHexString(
      table.theme?.boardColor ?? '#2d3e50'
    );
    boardMat.specularColor = new Color3(0.05, 0.05, 0.05); // Very matte
    board.material = boardMat;

    // Wall material
    const wallHeight = 0.5;
    const wallThickness = 0.15;
    const wallMat = new StandardMaterial('wallMat', this.scene);
    wallMat.diffuseColor = Color3.FromHexString(
      table.theme?.wallColor ?? '#4a5a6a'
    );
    wallMat.specularColor = new Color3(0.1, 0.1, 0.1);

    // Left wall
    const leftWall = MeshBuilder.CreateBox(
      'leftWall',
      { width: wallThickness, height: wallHeight, depth: table.length },
      this.scene
    );
    leftWall.position.set(-table.width / 2, wallHeight / 2, 0);
    leftWall.material = wallMat;
    walls.push(leftWall);

    // Right wall
    const rightWall = MeshBuilder.CreateBox(
      'rightWall',
      { width: wallThickness, height: wallHeight, depth: table.length },
      this.scene
    );
    rightWall.position.set(table.width / 2, wallHeight / 2, 0);
    rightWall.material = wallMat;
    walls.push(rightWall);

    // Back wall
    const backWall = MeshBuilder.CreateBox(
      'backWall',
      { width: table.width + wallThickness * 2, height: wallHeight, depth: wallThickness },
      this.scene
    );
    backWall.position.set(0, wallHeight / 2, -table.length / 2);
    backWall.material = wallMat;
    walls.push(backWall);

    // Front wall (with gap for drain)
    const frontWallWidth = (table.width - 1.5) / 2;
    const frontLeft = MeshBuilder.CreateBox(
      'frontLeft',
      { width: frontWallWidth, height: wallHeight, depth: wallThickness },
      this.scene
    );
    frontLeft.position.set(
      -table.width / 4 - 0.375,
      wallHeight / 2,
      table.length / 2
    );
    frontLeft.material = wallMat;
    walls.push(frontLeft);

    const frontRight = MeshBuilder.CreateBox(
      'frontRight',
      { width: frontWallWidth, height: wallHeight, depth: wallThickness },
      this.scene
    );
    frontRight.position.set(
      table.width / 4 + 0.375,
      wallHeight / 2,
      table.length / 2
    );
    frontRight.material = wallMat;
    walls.push(frontRight);

    // Ball
    const ball = MeshBuilder.CreateSphere(
      'ball',
      { diameter: 0.4, segments: 24 },
      this.scene
    );
    ball.position.set(
      table.spawnPosition.x,
      table.spawnPosition.y,
      table.spawnPosition.z
    );

    const ballMat = new StandardMaterial('ballMat', this.scene);
    ballMat.diffuseColor = new Color3(0.9, 0.9, 0.95);
    ballMat.specularColor = new Color3(0.2, 0.2, 0.2);
    
    // Marble texture for visual spin
    const textureSize = 128;
    const dynamicTexture = new DynamicTexture(
      'ballTexture',
      textureSize,
      this.scene,
      true
    );
    const ctx = dynamicTexture.getContext();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, textureSize, textureSize);
    
    // Draw grid
    ctx.strokeStyle = '#eeeeee';
    ctx.lineWidth = 2;
    for (let i = 0; i <= textureSize; i += textureSize / 4) {
      ctx.beginPath();
      ctx.moveTo(i, 0); ctx.lineTo(i, textureSize);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i); ctx.lineTo(textureSize, i);
      ctx.stroke();
    }
    
    // Draw some accent dots
    ctx.fillStyle = '#cbd5e0';
    const dotSize = 8;
    for (let x = 0; x < 4; x++) {
      for (let y = 0; y < 4; y++) {
        if ((x + y) % 2 === 0) {
          ctx.beginPath();
          ctx.arc(
            x * (textureSize / 4) + textureSize / 8,
            y * (textureSize / 4) + textureSize / 8,
            dotSize, 0, Math.PI * 2
          );
          ctx.fill();
        }
      }
    }
    
    dynamicTexture.update();
    ballMat.diffuseTexture = dynamicTexture;
    ball.material = ballMat;

    // Blob Shadow (Fake Shadow for performance)
    const blobShadow = MeshBuilder.CreateDisc('blobShadow', { radius: 0.22 }, this.scene);
    blobShadow.position.y = 0.005; // Just above board
    blobShadow.rotation.x = Math.PI / 2;
    const shadowMat = new StandardMaterial('shadowMat', this.scene);
    shadowMat.diffuseColor = Color3.Black();
    shadowMat.alpha = 0.35;
    shadowMat.specularColor = Color3.Black();
    blobShadow.material = shadowMat;

    // Targets
    for (const targetDef of table.targets) {
      const target = this.createTarget(targetDef, table.theme?.accentColor);
      targets.set(targetDef.id, target);
    }

    // Holes
    for (const holeDef of table.holes) {
      const hole = this.createHole(holeDef);
      holes.set(holeDef.id, hole);
    }

    // Finish gate
    const finish = this.createFinishGate(table);

    this.tableMeshes = { board, walls, ball, targets, holes, finish, blobShadow };
    return this.tableMeshes;
  }

  /** Create a target bumper */
  private createTarget(def: TargetDef, accentColor?: string): Mesh {
    const target = MeshBuilder.CreateCylinder(
      `target-${def.id}`,
      {
        diameter: 0.5,
        height: 0.3,
        tessellation: 24,
      },
      this.scene
    );
    target.position.set(def.position.x, def.position.y + 0.15, def.position.z);

    const mat = new StandardMaterial(`target-${def.id}-mat`, this.scene);
    const baseColor = Color3.FromHexString(accentColor ?? '#ffd700');
    mat.diffuseColor = baseColor;
    mat.emissiveColor = baseColor.scale(0.2); // Soft self-glow
    mat.specularColor = new Color3(0.1, 0.1, 0.1);
    target.material = mat;

    return target;
  }

  /** Create a hole/hazard */
  private createHole(def: HoleDef): Mesh {
    const hole = MeshBuilder.CreateDisc(
      `hole-${def.id}`,
      {
        radius: def.radius,
        tessellation: 32,
      },
      this.scene
    );
    hole.position.set(def.position.x, 0.002, def.position.z);
    hole.rotation.x = Math.PI / 2;

    const mat = new StandardMaterial(`hole-${def.id}-mat`, this.scene);
    mat.diffuseColor = new Color3(0.02, 0.02, 0.04); // Deep dark blue/black
    mat.specularColor = Color3.Black();
    hole.material = mat;

    return hole;
  }

  /** Create finish gate */
  private createFinishGate(table: ITableDef): Mesh {
    const finish = MeshBuilder.CreateBox(
      'finish',
      { width: 1.2, height: 0.4, depth: 0.3 },
      this.scene
    );
    finish.position.set(
      table.finishPosition.x,
      0.2,
      table.finishPosition.z
    );

    const mat = new StandardMaterial('finish-mat', this.scene);
    mat.diffuseColor = new Color3(0.3, 0.7, 0.4);
    mat.emissiveColor = new Color3(0.1, 0.2, 0.1);
    mat.specularColor = Color3.Black();
    finish.material = mat;

    return finish;
  }

  /** Update target visual when hit */
  setTargetHit(targetId: string, hit: boolean): void {
    const mesh = this.tableMeshes?.targets.get(targetId);
    if (!mesh) return;

    const mat = mesh.material as StandardMaterial;
    if (hit) {
      mat.diffuseColor = new Color3(0.4, 0.4, 0.45);
      mat.emissiveColor = Color3.Black();
    } else {
      const baseColor = Color3.FromHexString(this.accentColor);
      mat.diffuseColor = baseColor;
      mat.emissiveColor = baseColor.scale(0.2);
    }
  }

  /** Update finish gate visual when open */
  setGateOpen(open: boolean): void {
    const mesh = this.tableMeshes?.finish;
    if (!mesh) return;

    const mat = mesh.material as StandardMaterial;
    if (open) {
      mesh.scaling.y = 0.1;
      mesh.position.y = 0.02;
      mat.diffuseColor = new Color3(0.4, 1.0, 0.5);
      mat.emissiveColor = new Color3(0.2, 0.4, 0.2);
    } else {
      mesh.scaling.y = 1;
      mesh.position.y = 0.2;
      mat.diffuseColor = new Color3(0.8, 0.3, 0.3);
      mat.emissiveColor = new Color3(0.3, 0.1, 0.1);
    }
  }

  /** Get ball mesh */
  getBallMesh(): Mesh | null {
    return this.tableMeshes?.ball ?? null;
  }

  /** Focus camera on table center */
  focusOnTable(table: ITableDef): void {
    const screenW = this.engine.getRenderWidth();
    const screenH = this.engine.getRenderHeight();
    const aspectRatio = screenW / screenH;

    this.camera.target = new Vector3(0, 0, 0);

    const fov = this.camera.fov;
    const halfFovV = fov / 2;
    const halfFovH = Math.atan(Math.tan(halfFovV) * aspectRatio);

    const distForWidth = (table.width / 2) / Math.tan(halfFovH);
    const distForLength = (table.length / 2) / Math.tan(halfFovV);

    const padding = 1.15;
    this.camera.radius = Math.max(distForWidth, distForLength) * padding;
  }

  /** Get active mesh count (for debug display) */
  get activeMeshCount(): number {
    return this.scene.getActiveMeshes().length;
  }

  /** Render one frame */
  render(): void {
    // Update blob shadow to follow ball
    if (this.tableMeshes?.ball && this.tableMeshes?.blobShadow) {
      const ballPos = this.tableMeshes.ball.position;
      this.tableMeshes.blobShadow.position.x = ballPos.x;
      this.tableMeshes.blobShadow.position.z = ballPos.z;
      
      // Scale shadow based on height (slightly smaller if ball bounces high)
      const heightFactor = Math.max(0, 1 - (ballPos.y - 0.2) * 2);
      this.tableMeshes.blobShadow.scaling.setAll(heightFactor);
      (this.tableMeshes.blobShadow.material as StandardMaterial).alpha = 0.35 * heightFactor;
    }
    
    this.scene.render();
  }

  /** Clean up */
  dispose(): void {
    this.resizeObserver?.disconnect();
    this.scene.dispose();
    this.engine.dispose();
  }
}